import { useContext, useEffect } from "react";
import { Button, TitleBar } from "@shopify/app-bridge/actions";
import { AppBridgeContext } from "../components/AppBridgeProvider";
import { openSupportEmail } from "../config/appBranding";

/**
 * Shopify admin title bar (replaces Polaris Page title / primaryAction).
 * Support is primary when it is the only action; otherwise secondary (left of primary).
 * @param {string} title
 * @param {{ label: string, onClick: () => void } | null} [primaryAction]
 * @param {{ label: string, onClick: () => void } | null} [breadcrumb]
 */
export function useAppTitleBar({ title, primaryAction = null, breadcrumb = null }) {
  const app = useContext(AppBridgeContext);
  const primaryLabel = primaryAction?.label ?? null;
  const primaryClick = primaryAction?.onClick ?? null;
  const breadcrumbLabel = breadcrumb?.label ?? null;
  const breadcrumbClick = breadcrumb?.onClick ?? null;

  useEffect(() => {
    if (!app || !title) return undefined;

    let titlebar = null;
    let unsubscribePrimary;
    let unsubscribeSupport;
    let unsubscribeBreadcrumb;

    try {
      const options = { title };
      const buttons = {};
      const hasPagePrimary = Boolean(primaryLabel && primaryClick);

      const supportButton = Button.create(app, { label: "Support" });
      unsubscribeSupport = supportButton.subscribe(Button.Action.CLICK, openSupportEmail);

      if (hasPagePrimary) {
        const primaryButton = Button.create(app, { label: primaryLabel });
        unsubscribePrimary = primaryButton.subscribe(Button.Action.CLICK, primaryClick);
        buttons.primary = primaryButton;
        buttons.secondary = [supportButton];
      } else {
        buttons.primary = supportButton;
      }

      options.buttons = buttons;

      if (breadcrumbLabel && breadcrumbClick) {
        const breadcrumbButton = Button.create(app, { label: breadcrumbLabel });
        unsubscribeBreadcrumb = breadcrumbButton.subscribe(Button.Action.CLICK, breadcrumbClick);
        options.breadcrumbs = breadcrumbButton;
      }

      titlebar = TitleBar.create(app, options);
    } catch {
      /* older App Bridge */
    }

    return () => {
      try {
        if (typeof unsubscribePrimary === "function") unsubscribePrimary();
        if (typeof unsubscribeSupport === "function") unsubscribeSupport();
        if (typeof unsubscribeBreadcrumb === "function") unsubscribeBreadcrumb();
        titlebar?.unsubscribe?.();
      } catch {
        /* noop */
      }
    };
  }, [app, title, primaryLabel, primaryClick, breadcrumbLabel, breadcrumbClick]);
}
